/**
 * Pose do personagem enquanto o Secretário trabalha na conversa ao vivo.
 *
 * O controlador da conversa diz o que está acontecendo de fato (pensando com
 * calma, pesquisando, escrevendo, esperando o modelo responder) e o rig do
 * personagem lê, a cada quadro, quanto de cada gesto mostrar. Os gestos entram e
 * saem devagar e ficam um tempo mínimo na tela: uma consulta de meio segundo
 * não vira um tique de mão.
 *
 * Pensar tem mais de um jeito, sorteado a cada vez e nunca o mesmo duas vezes
 * seguidas (quem conversa muito não vê sempre a mão no queixo):
 * - `queixo`: mão no queixo, olhar para cima e para o lado, boca de canto.
 * - `alto`: sem a mão; a cabeça sobe e inclina, o olhar vai para o alto do outro
 *   lado, uma sobrancelha sobe e os lábios se apertam de lado.
 * - `tempora`: o antebraço sobe girando e o indicador encosta na têmpora; o olhar
 *   vai para o alto do outro lado e uma sobrancelha sobe (pensando fundo).
 *
 * `celular`: o celular nas duas mãos, olhando para ele e digitando (pesquisa,
 * mensagem, documento): o aparelho sobe de baixo do quadro até o peito (número
 * `celular` do Caio). Quem não tem o celular no arquivo fica só com o olhar baixo.
 */
export type Pose = 'pensar' | 'celular';
/** Gestos que o rig desenha. */
export type Gesture = 'queixo' | 'alto' | 'tempora' | 'celular';
export type PoseWeights = Record<Gesture, number>;

/** Jeitos de pensar, com o peso do sorteio (a mão no queixo continua a mais comum). */
const DEFAULT_THINKING: { gesture: Exclude<Gesture, 'celular'>; weight: number }[] = [
  { gesture: 'queixo', weight: 1 },
  { gesture: 'alto', weight: 1 },
  { gesture: 'tempora', weight: 1 },
];
let THINKING = DEFAULT_THINKING;
const GESTURES: Gesture[] = ['queixo', 'alto', 'tempora', 'celular'];

/**
 * Jeitos de pensar que o personagem da vez tem no arquivo (o rig avisa ao montar): quem não
 * tem a peça não sorteia o gesto.
 */
export function setThinkingGestures(gestures: Exclude<Gesture, 'celular'>[]): void {
  THINKING = gestures.length
    ? gestures.map((gesture) => ({ gesture, weight: 1 }))
    : DEFAULT_THINKING;
}

/** Tempo mínimo na tela depois que a pose entra (segundos). */
const MIN_HOLD = 1.8;
/** Constantes de tempo da subida e da descida (segundos). */
const RISE = 0.32;
const FALL = 0.45;

type State = {
  target: Gesture | null;
  /** Último jeito de pensar mostrado (o próximo sorteio evita repetir). */
  lastThinking: Exclude<Gesture, 'celular'> | null;
  /** Pose pedida pelo controlador (o sorteio só acontece quando ela muda). */
  asked: Pose | null;
  shownAt: number;
  weights: PoseWeights;
  last: number;
};

const empty = (): PoseWeights => ({ queixo: 0, alto: 0, tempora: 0, celular: 0 });

const state: State = {
  target: null,
  lastThinking: null,
  asked: null,
  shownAt: 0,
  weights: empty(),
  last: 0,
};

const now = () => performance.now() / 1000;

let random: () => number = Math.random;
/** Para testes: sorteio previsível. */
export function setPoseRandom(fn: () => number): void {
  random = fn;
}

function pickThinking(): Exclude<Gesture, 'celular'> {
  const options = THINKING.filter((item) => item.gesture !== state.lastThinking);
  const total = options.reduce((sum, item) => sum + item.weight, 0);
  let roll = random() * total;
  for (const item of options) {
    roll -= item.weight;
    if (roll <= 0) return item.gesture;
  }
  return options[options.length - 1]!.gesture;
}

/** O que está acontecendo agora; `null` solta a pose (respeitando o tempo mínimo). */
export function setPose(next: Pose | null): void {
  if (next === state.asked) return;
  state.asked = next;
  if (next === null) {
    state.target = null;
    return;
  }
  if (next === 'celular') {
    state.target = 'celular';
  } else {
    // Ainda na tela (voltou a pensar logo depois): mantém o mesmo jeito, sem trocar de gesto.
    const showing =
      state.lastThinking && state.weights[state.lastThinking] > 0.3 ? state.lastThinking : null;
    const gesture = showing ?? pickThinking();
    state.lastThinking = gesture;
    state.target = gesture;
  }
  state.shownAt = now();
}

/** Peso de cada gesto neste quadro (0 a 1), já suavizado. */
export function poseFrame(): PoseWeights {
  const t = now();
  const dt = state.last ? Math.min(0.1, t - state.last) : 0;
  state.last = t;
  // Solta só depois do tempo mínimo: a pose que acabou de entrar segura um pouco.
  const holding = state.target === null && t - state.shownAt < MIN_HOLD;
  for (const gesture of GESTURES) {
    const active = state.target === gesture || (holding && state.weights[gesture] > 0.5);
    const goal = active ? 1 : 0;
    const tau = goal > state.weights[gesture] ? RISE : FALL;
    state.weights[gesture] += (goal - state.weights[gesture]) * (1 - Math.exp(-dt / tau));
    if (state.weights[gesture] < 0.002) state.weights[gesture] = 0;
  }
  return { ...state.weights };
}

/** Fim da conversa (ou testes): sem pose. */
export function resetPose(): void {
  state.target = null;
  state.lastThinking = null;
  state.asked = null;
  state.shownAt = 0;
  state.weights = empty();
  state.last = 0;
}
